import type { KarlCustomPayload } from "@/components/document-chat/services/streamEventTypes";
import { KarlCustomEvent, KarlNodeRaw, ProcessingNode, StreamPhase } from "@/constants/enums";
import { useDocumentChatRuntime } from "@/store/documentChatRuntime";
// Types are globally available from @/types/chat-sse.d.ts
import type { DocumentChatRuntime } from "../types";

/**
 * Handles messages channel events for token streaming
 *
 * @param payload - The messages event payload
 * @param session - The current stream session
 * @param runtime - The document chat runtime store interface
 * @param currentNodeState - Current processing node state
 * @param processingComplete - Whether processing stages are complete
 */
export function handleMessagesEvent(
  payload: KarlCustomPayload,
  session: StreamSession,
  runtime: DocumentChatRuntime,
  currentNodeState: string,
  processingComplete: boolean
): void {
  const type = String(payload.type ?? "");

  if (type === KarlCustomEvent.CHUNK) {
    let token = typeof payload.content === "string" ? payload.content : "";
    if (!token) {
      return;
    }

    // *** Stream ownership + deduplication to avoid showing the answer twice
    // Identify the incoming producer (tool_run_id) for this chunk
    const incomingToolRunId = payload.tool_run_id ?? null;

    // Lock ownership to the first producer that emits answer chunks
    if (!session.activeMessageToolRunId && incomingToolRunId) {
      session.activeMessageToolRunId = incomingToolRunId;
    }

    // If another producer emits chunks, dedupe by trimming or ignoring overlap
    if (
      incomingToolRunId &&
      session.activeMessageToolRunId &&
      incomingToolRunId !== session.activeMessageToolRunId
    ) {
      const { rawStreamBuffer = "" } = useDocumentChatRuntime.getState();

      // Drop exact duplicates (same chunk repeated from a different producer)
      if (rawStreamBuffer.endsWith(token)) {
        return;
      }

      // When the new producer is replaying the full answer, trim overlapping prefix so we only append new content
      if (token.startsWith(rawStreamBuffer)) {
        token = token.slice(rawStreamBuffer.length);
      }

      // Adopt the most recent producer so we don't lose subsequent chunks
      session.activeMessageToolRunId = incomingToolRunId;

      if (!token) {
        return;
      }
    }

    // *** Route chunks based on current node to ensure proper display
    // QuestionGenerator and ContextRetriever chunks go to thinking area
    // AnswerQuestion and FinalizeAnswer chunks go to streaming answer area
    const nodeLower = currentNodeState.toLowerCase();

    // Priority 1: Explicit thinking phase - always route to thinking
    if (
      session.thinkingActive ||
      nodeLower === KarlNodeRaw.QuestionGenerator ||
      nodeLower === KarlNodeRaw.ContextRetriever
    ) {
      runtime.appendThinking(token);
      return;
    }

    // Priority 2: Explicit answer phase or already streaming answer
    if (
      nodeLower === KarlNodeRaw.AnswerQuestion ||
      nodeLower === KarlNodeRaw.FinalizeAnswer ||
      session.answerToolActive ||
      session.answerHasStreamed
    ) {
      if (!session.answerHasStreamed) {
        session.answerHasStreamed = true;
        runtime.markAnswerStart();
        runtime.update({ phase: StreamPhase.ANSWERING, currentNode: ProcessingNode.ANSWER_QUESTION });
      }
      runtime.appendStreamingToken(token);
      return;
    }

    // Priority 3: Smart fallback based on processing stage
    // Only assume chunks are answer chunks if processing stages are complete
    // Otherwise, route to thinking (likely late-arriving question generation chunks)
    if (processingComplete) {
      if (!session.answerHasStreamed) {
        session.answerHasStreamed = true;
        runtime.markAnswerStart();
        runtime.update({ phase: StreamPhase.ANSWERING, currentNode: ProcessingNode.ANSWER_QUESTION });
      }
      runtime.appendStreamingToken(token);
    } else {
      runtime.appendThinking(token);
    }
  }
}
